import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import type { AssetTypeEnum, FrequencyEnum, OptionData, PortfolioNodeDesignationEnum } from 'venn-api';
import { ColorUtils, GetColor, Icon, Tooltip } from 'venn-ui-kit';
import StrategyInput from './strategy/StrategyInput';
import { Constants } from './Layout';
import { type HistoricalPortfolioNodeAllocationType, toQaClassName } from 'venn-utils';
import { getFixedWidthElementsWidth } from './allocation-panel-utils';
import { AllocationItemNameTooltip, type AllocationItemNameTooltipProps } from './AllocationItemNameTooltip';
import { AllocationsSnappedIcon } from '../blocks/components/secondary/historical-portfolio/HistoricalPortfolioStudioHeaderTooltips';
import { ALLOCATIONS_SNAPPED_MESSAGE } from 'venn-state';
import { CashIcon } from '../common/CashIcon';
import { OptionIcon } from '../common/OptionIcon';
import { AssetBadge } from '../asset-badge';

export const GhostFundNameClass = 'ghost-fund-name';
export const OutOfSubtreeNameClass = 'out-of-subtree-item-name';

export interface AllocationItemNameProps {
  originalName?: string;
  name: string;
  width: number;
  isGhost: boolean;
  isEditing: boolean;
  hasAddFund: boolean;
  isSelected: boolean;
  isCollapsed: boolean;
  isOutsideOfSelectedSubtree: boolean;
  investmentTimeRange?: string;
  isMissingReturnsMessage?: JSX.Element;
  onSelectStrategyClick?: () => void;
  onAddFundClick: (() => void) | undefined;
  onTriangleClick: (() => void) | undefined;
  onEditNameClick: ((value: string) => void) | undefined;
  investmentSymbol?: string;
  hideComparisonColumn: boolean;
  allocationItemConfig: AllocationItemNameTooltipProps['allocationItemConfig'];
  isAllocationsForwardFilled: boolean;
  historicalPortfolioNodeAllocationType: HistoricalPortfolioNodeAllocationType | undefined;
  isRoot: boolean;
  designation: PortfolioNodeDesignationEnum;
  isNegativeAssets: boolean;
  optionData: OptionData | undefined;
  frequency: FrequencyEnum | undefined;
  assetType: AssetTypeEnum | undefined;
  isIncompleteTaxInfo: boolean;
}

class AllocationItemName extends PureComponent<AllocationItemNameProps> {
  render() {
    const {
      name,
      width,
      isGhost,
      isEditing,
      hasAddFund,
      isSelected,
      isCollapsed,
      isOutsideOfSelectedSubtree,
      onSelectStrategyClick,
      onAddFundClick,
      onTriangleClick,
      onEditNameClick,
      hideComparisonColumn,
      allocationItemConfig,
      isAllocationsForwardFilled,
      historicalPortfolioNodeAllocationType,
      isRoot,
      designation,
      isNegativeAssets,
      optionData,
      frequency,
      assetType,
      isIncompleteTaxInfo,
    } = this.props;
    const isStrategy = allocationItemConfig._tag === 'strategy';
    if (isEditing && onEditNameClick) {
      return <StrategyInput name={name} onChange={onEditNameClick} />;
    }
    const fixedWidthElementsWidth = getFixedWidthElementsWidth({ isStrategy, hasAddFund, hideComparisonColumn });
    const fixedElementsForSelectedBackgroundWidth = isStrategy
      ? fixedWidthElementsWidth -
        Constants.NAME_MARGIN -
        Constants.TRIANGLE_WIDTH -
        (hasAddFund ? Constants.ADD_FUND_PLUS_WIDTH : 0)
      : fixedWidthElementsWidth - Constants.NAME_MARGIN;

    const isSelectingDisabled = onSelectStrategyClick === undefined;
    const testqaClass = toQaClassName(name);

    const isCashNode = designation === 'CASH';

    return (
      <ItemName
        tabIndex={0}
        isGhost={isGhost}
        isStrategy={isStrategy}
        isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
        className={
          isGhost && !isStrategy && !isOutsideOfSelectedSubtree
            ? GhostFundNameClass
            : !isGhost && isOutsideOfSelectedSubtree
              ? OutOfSubtreeNameClass
              : testqaClass
        }
        disabled={isGhost || isSelected || isSelectingDisabled}
        aria-label={isSelectingDisabled ? undefined : `Select ${name}`}
        onClick={onSelectStrategyClick}
      >
        {isSelected && (
          <div className="relative">
            <SelectedBackground width={width - fixedElementsForSelectedBackgroundWidth} />
          </div>
        )}

        {isStrategy && (
          <Triangle
            isGhost={isGhost}
            isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
            aria-label={`${isCollapsed ? 'Show' : 'Hide'} children of strategy ${name}`}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onTriangleClick?.();
            }}
          >
            <Icon type={isCollapsed ? 'caret-right' : 'caret-down'} />
          </Triangle>
        )}

        {isIncompleteTaxInfo && (
          <Tooltip usePortal content="Incomplete tax information">
            <Icon type="diamond-exclamation" className="text-venn-orange mr-[4px] shrink-0 z-[1]" />
          </Tooltip>
        )}

        <AllocationItemNameTooltip
          originalName={this.props.originalName}
          name={name}
          isGhost={isGhost}
          allocationItemConfig={allocationItemConfig}
          isSelected={isSelected}
          isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
          investmentTimeRange={designation === 'CASH' ? undefined : this.props.investmentTimeRange}
          isMissingReturnsMessage={designation === 'CASH' ? undefined : this.props.isMissingReturnsMessage}
          investmentSymbol={designation === 'CASH' ? undefined : this.props.investmentSymbol}
          hasAddFund={hasAddFund}
          historicalPortfolioNodeAllocationType={historicalPortfolioNodeAllocationType}
          optionData={optionData}
          frequency={frequency}
        >
          {isAllocationsForwardFilled && (
            <Tooltip usePortal content={ALLOCATIONS_SNAPPED_MESSAGE}>
              <AllocationsSnappedIcon iconLabel="allocations snapped icon" />
            </Tooltip>
          )}
          {isCashNode && (
            <CashIcon isNegativeAssets={isNegativeAssets} disabled={isGhost || isOutsideOfSelectedSubtree} />
          )}
          {optionData && (
            <OptionIcon optionType={optionData.optionType} disabled={isGhost || isOutsideOfSelectedSubtree} />
          )}
        </AllocationItemNameTooltip>

        {assetType ? <AssetBadge assetType={assetType} size={14} className="shrink-0 mb-[6px] z-[1]" /> : null}

        {hasAddFund && (
          <AddFund
            isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
            aria-label="Add investment"
            onClick={
              !isOutsideOfSelectedSubtree
                ? (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onAddFundClick?.();
                  }
                : undefined
            }
            data-testid={isRoot ? 'qa-add-to-root' : 'qa-add-to-strategy'}
          >
            <Icon type="plus" />
          </AddFund>
        )}
      </ItemName>
    );
  }
}
export default AllocationItemName;

const Triangle = styled.button<{ isGhost: boolean; isOutsideOfSelectedSubtree: boolean }>`
  position: relative;
  font-size: 16px;
  left: -4.5px;
  width: ${Constants.TRIANGLE_WIDTH}px;
  min-width: ${Constants.TRIANGLE_WIDTH}px;
  text-align: center;
  flex-shrink: 0;

  &&&:hover {
    cursor: pointer;
  }

  ${({ isGhost, isOutsideOfSelectedSubtree }) => css`
    color: ${isGhost || isOutsideOfSelectedSubtree ? GetColor.GreyScale.Grey40 : GetColor.Black};
  `}
`;

const Selectable = styled.span<{ disabled: boolean }>`
  &:hover {
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  }
`;

const ItemName = styled(Selectable)<{
  isGhost: boolean;
  isStrategy: boolean;
  isOutsideOfSelectedSubtree: boolean;
}>`
  display: flex;
  gap: 5px;
  align-items: center;
  height: 30px;
  min-width: 0;
  flex-grow: 1;

  ${({ isGhost, isOutsideOfSelectedSubtree }) =>
    isGhost || isOutsideOfSelectedSubtree
      ? css`
          color: ${GetColor.GreyScale.Grey40};
        `
      : css`
          color: ${GetColor.Black};
        `}
  ${({ isStrategy }) =>
    isStrategy
      ? css`
          font-weight: bold;
          font-size: 12px;
        `
      : css`
          font-size: 14px;
        `}
  & > * {
    min-width: 0;
  }
`;

const SelectedBackground = styled.div<{ width: number }>`
  height: 30px;
  background-color: ${ColorUtils.opacifyFrom(GetColor.Primary.Dark, 0.1)};
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  position: absolute;
  border: 1px solid ${GetColor.Primary.Dark};
  border-left: 0;
  left: -${Constants.NAME_MARGIN}px;
  padding-left: ${Constants.NAME_MARGIN}px;
  top: -15px;

  ${({ width }) => `width: ${width}px;`}
`;

const AddFund = styled.button<{ isOutsideOfSelectedSubtree: boolean }>`
  width: ${Constants.ADD_FUND_PLUS_WIDTH}px;
  min-width: ${Constants.ADD_FUND_PLUS_WIDTH}px;
  position: relative;
  top: -1px;
  color: ${({ isOutsideOfSelectedSubtree }) =>
    isOutsideOfSelectedSubtree ? GetColor.GreyScale.Grey40 : GetColor.GreyScale.Grey80};
  padding-left: 3px;
  padding-right: 5px;
  &&&:hover {
    ${({ isOutsideOfSelectedSubtree }) => (isOutsideOfSelectedSubtree ? 'cursor: default;' : 'cursor: pointer')}
  }
`;
